/**
 * Copy and per-OS data for the Remote setup flow (the three cards on /add-device).
 *
 * Kept out of the components so the wording can be reviewed without reading JSX. One copy for every
 * journey: the OAuth setup screen, the empty Devices dashboard and its "Add a device" dialog all
 * render the same flow, so changing a line here changes it everywhere.
 * Design decisions behind this copy: design/remote-onboarding/notes.md, v5 section.
 */

export type OsKey = "windows" | "mac" | "linux";

export interface OsCopy {
    /** Command suited to the selected shell; Windows explicitly invokes the .cmd shim. */
    command: string;
    setupCommand: string;
    setupSourceUrl: string;
    /** "Not on X?" in the switch. */
    label: string;
    /** Name in the switch links. */
    short: string;
    /** What the user calls the window. */
    term: string;
    /** Card 2 heading. */
    title: string;
    /** Step 1: how to open the window, split so the literal app name can be styled. */
    open: {
        before: string;
        literal: string;
        after: string;
    };
    /** Step 2: how to paste. */
    paste: string;
    /** Prompt character shown in the command block. */
    promptChar: string;
    /** The error a missing Node.js produces, verbatim, so it can be matched by eye. */
    npxError: string;
    /** Direct installer download. x64 by default — an arm64 build will not run on a normal PC. */
    nodeUrl: string;
    /** ARM alternative, where the platform has one. */
    nodeArmUrl?: string;
    /** How to describe the ARM machine in the alternative link. */
    armLabel?: string;
}

/**
 * Direct Node.js 24 downloads. Keep this version in sync with the public launchers' manual links.
 */
const NODE_BASE = "https://nodejs.org/dist/v24.22.1/node-v24.22.1";

// Always use main so setup and source links follow the latest public scripts.
const SETUP_RAW = "https://raw.githubusercontent.com/wonderwhy-er/DesktopCommanderMCP/main/scripts/remote";
const SETUP_SOURCE = "https://github.com/wonderwhy-er/DesktopCommanderMCP/blob/main/scripts/remote";

export const OS_COPY: Record<OsKey, OsCopy> = {
    // Windows first because it is the fallback when browser OS detection is inconclusive.
    windows: {
        command: "npx.cmd @wonderwhy-er/desktop-commander@latest remote",
        setupCommand: `irm -ErrorAction Stop ${SETUP_RAW}/remote-windows.ps1 | iex`,
        setupSourceUrl: `${SETUP_SOURCE}/remote-windows.ps1`,
        label: "Windows",
        short: "Windows",
        term: "PowerShell",
        title: "Paste this in PowerShell",
        open: {
            before: "Press ⊞ Win, type ",
            literal: "PowerShell",
            after: ", then press Enter.",
        },
        paste: "Click in PowerShell, press Ctrl V, then press Enter.",
        promptChar: ">",
        npxError: "'npx.cmd' is not recognized",
        nodeUrl: `${NODE_BASE}-x64.msi`,
        nodeArmUrl: `${NODE_BASE}-arm64.msi`,
        armLabel: "an ARM PC (Snapdragon)",
    },
    mac: {
        command: "npx @wonderwhy-er/desktop-commander@latest remote",
        setupCommand: `curl -fsSL ${SETUP_RAW}/remote-macos.sh | sh`,
        setupSourceUrl: `${SETUP_SOURCE}/remote-macos.sh`,
        label: "a Mac",
        short: "Mac",
        term: "Terminal",
        title: "Paste this in Terminal",
        open: {
            before: "Press ⌘ Space, type ",
            literal: "Terminal",
            after: ", then press Enter.",
        },
        paste: "Click in Terminal, press ⌘ V, then press Enter.",
        promptChar: "$",
        npxError: "command not found: npx",
        nodeUrl: `${NODE_BASE}.pkg`,
    },
    linux: {
        command: "npx @wonderwhy-er/desktop-commander@latest remote",
        setupCommand: `curl -fsSL ${SETUP_RAW}/remote-linux.sh | sh`,
        setupSourceUrl: `${SETUP_SOURCE}/remote-linux.sh`,
        label: "Linux",
        short: "Linux",
        term: "Terminal",
        title: "Paste this in your terminal",
        open: {
            before: "Press Ctrl Alt T, or search your apps for ",
            literal: "Terminal",
            after: ".",
        },
        paste: "Click in Terminal, press Ctrl Shift V, then press Enter.",
        promptChar: "$",
        npxError: "npx: command not found",
        nodeUrl: `${NODE_BASE}-linux-x64.tar.xz`,
        nodeArmUrl: `${NODE_BASE}-linux-arm64.tar.xz`,
        armLabel: "an ARM machine",
    },
};

export const WINDOWS_CMD_COPY: OsCopy = {
    ...OS_COPY.windows,
    // CMD starts PowerShell to run the same setup command.
    setupCommand: `powershell.exe -NoProfile -Command "${OS_COPY.windows.setupCommand}"`,
    term: "Command Prompt",
    title: "Paste this in Command Prompt",
    open: {
        before: "Press ⊞ Win, type ",
        literal: "Command Prompt",
        after: ", then press Enter.",
    },
    paste: "Click in Command Prompt, press Ctrl V, then press Enter.",
};

/** Detect the current browser OS when possible; Windows is the fallback for unknown platforms. */
export function detectOs(platformOverride?: string): OsKey {
    let platform: string;
    if (platformOverride !== undefined) {
        platform = platformOverride;
    } else if (typeof navigator !== "undefined") {
        const browserNavigator = navigator as Navigator & {
            userAgentData?: {
                platform?: string;
            };
        };
        platform = browserNavigator.userAgentData?.platform || navigator.platform || navigator.userAgent || "";
    } else {
        platform = "";
    }
    platform = platform.toLowerCase();

    if (platform.includes("mac")) {
        return "mac";
    }
    if (platform.includes("linux")) {
        return "linux";
    }
    if (platform.includes("win")) {
        return "windows";
    }

    return "windows";
}

/**
 * Handed to the user's own AI when the terminal defeats them. It is the only help that works for
 * someone who has never opened a terminal, and it costs us nothing.
 */
export const HELP_PROMPT =
    "Walk me through installing Desktop Commander Remote so my AI chat can work with the files on my own computer. " +
    "Assume I have never opened a terminal and do not know what PowerShell, Node.js or npx are.";

/** Prefilled-composer links. Not the connector URLs in consts.ts — those open the app listing. */
export const CLIENT_COMPOSER_URL = {
    ChatGPT: "https://chat.openai.com/?q=",
    Claude: "https://claude.ai/new?q=",
} as const;

export type ClientName = keyof typeof CLIENT_COMPOSER_URL;

/** The first thing worth trying once a device is connected. Read-only on purpose. */
export const FIRST_PROMPT = "Use Desktop Commander Remote to find the 5 largest files in my Downloads folder";

/**
 * The verify tab is opened by the CLI, so it never sees the `client` query param. The OAuth setup
 * tab writes the assistant here (same origin) and the finish line reads it back. A different browser
 * simply gets null and the generic wording.
 *
 * Only the OAuth journey writes it: this is localStorage, shared across the origin rather than per
 * tab, so the dashboard's copy of the flow — which never has a client — must not clear what a live
 * OAuth run put here.
 */
const CLIENT_STORAGE_KEY = "dc.remote.client";

export function rememberClient(client: ClientName | null): void {
    try {
        if (client) {
            window.localStorage.setItem(CLIENT_STORAGE_KEY, client);
        } else {
            // An unrecognised client has to clear the key, not skip the write. Leaving the previous
            // run's value behind makes the verify tab claim the device "now works with Claude" for
            // someone who is connecting from Grok, Cursor or anything else we cannot name.
            window.localStorage.removeItem(CLIENT_STORAGE_KEY);
        }
    } catch {
        // Private mode or blocked storage — the finish line just stays generic.
    }
}

export function rememberedClient(): ClientName | null {
    try {
        return asClientName(window.localStorage.getItem(CLIENT_STORAGE_KEY));
    } catch {
        return null;
    }
}

export function composerLink(client: ClientName, prompt: string): string {
    return CLIENT_COMPOSER_URL[client] + encodeURIComponent(prompt);
}

/**
 * Narrows the `client` query param (set server-side from the OAuth redirect host) to one we can
 * link to. Anything else — a desktop app, an IDE, a test client — stays null and the UI keeps the
 * generic wording and offers both assistants.
 */
export function asClientName(value?: string | null): ClientName | null {
    return value && value in CLIENT_COMPOSER_URL ? (value as ClientName) : null;
}
